import {useEffect, useMemo, useRef, useState} from 'react';
import {isEqual} from "lodash";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {filterOperations} from "../../../model/filterOperations";
import {addErrorNotification} from "../../../state/notification/notifications";
import {dateToString, stringToDate} from "common/src/utils/DateUtils";

// Shared logic for the Static/Dynamic filter cards. Filters aren't split into separate arrays -
// they live together, distinguished by each entry's valueMode - so both cards read and write the
// same form fields. This hook owns that shared state and exposes only the slice relevant to the
// caller's fixed mode.
const useFilterColumnsCard = ({
    options, numericFilterFormField, stringFilterFormField, timeRangeFilterFormField,
    timestampFilterFormField, mode
}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [tempColumn, setTempColumn] = useState("");
    const [tempArrayOperation, setTempArrayOperation] = useState("");
    const [tempFilterOperation, setTempFilterOperation] = useState("");
    // For TIMESTAMP columns this holds {keyword} | {start, end} | {timestamp}, depending on
    // whether tempFilterOperation is BETWEEN or a single point. Every other column type just
    // holds a plain scalar value.
    const [tempFilterValue, setTempFilterValue] = useState("");

    const [numericFilters, setNumericFilters] = useState(numericFilterFormField?.value || []);
    const [stringFilters, setStringFilters] = useState(stringFilterFormField?.value || []);
    const [timestampRangeFilters, setTimeRangeFilters] = useState(timeRangeFilterFormField?.value || []);
    const [timestampFilters, setTimestampFilters] = useState(timestampFilterFormField?.value || []);

    const [externalNumericFilters, setExternalNumericFilters] = useState(numericFilterFormField?.value || []);
    const [externalStringFilters, setExternalStringFilters] = useState(stringFilterFormField?.value || []);
    const [externalTimestampRangeFilters, setExternalTimeRangeFilters] = useState(timeRangeFilterFormField?.value || []);
    const [externalTimestampFilters, setExternalTimestampFilters] = useState(timestampFilterFormField?.value || []);

    const previousNumericFilters = useRef(numericFilterFormField?.value || []);
    const previousStringFilters = useRef(stringFilterFormField?.value || []);
    const previousTimeRangeFilters = useRef(timeRangeFilterFormField?.value || []);
    const previousTimestampFilters = useRef(timestampFilterFormField?.value || []);

    useEffect(() => {
        if (numericFilterFormField.value !== previousNumericFilters.current) {
            setNumericFilters(numericFilterFormField.value || []);
            setExternalNumericFilters(numericFilterFormField.value || []);
        }
    }, [numericFilterFormField.value]);

    useEffect(() => {
        if (previousNumericFilters.current !== numericFilters) {
            previousNumericFilters.current = numericFilters;
            if (!isEqual(numericFilters, externalNumericFilters)) {
                numericFilterFormField.setValue(numericFilters, undefined, true);
            }
        }
    }, [numericFilters]);

    useEffect(() => {
        if (stringFilterFormField.value !== previousStringFilters.current) {
            setStringFilters(stringFilterFormField.value || []);
            setExternalStringFilters(stringFilterFormField.value || []);
        }
    }, [stringFilterFormField.value]);

    useEffect(() => {
        if (previousStringFilters.current !== stringFilters) {
            previousStringFilters.current = stringFilters;
            if (!isEqual(stringFilters, externalStringFilters)) {
                stringFilterFormField.setValue(stringFilters, undefined, true);
            }
        }
    }, [stringFilters]);

    useEffect(() => {
        if (timeRangeFilterFormField.value !== previousTimeRangeFilters.current) {
            setTimeRangeFilters(timeRangeFilterFormField.value || []);
            setExternalTimeRangeFilters(timeRangeFilterFormField.value || []);
        }
    }, [timeRangeFilterFormField.value]);

    useEffect(() => {
        if (previousTimeRangeFilters.current !== timestampRangeFilters) {
            previousTimeRangeFilters.current = timestampRangeFilters;
            if (!isEqual(timestampRangeFilters, externalTimestampRangeFilters)) {
                timeRangeFilterFormField.setValue(timestampRangeFilters, undefined, true);
            }
        }
    }, [timestampRangeFilters]);

    useEffect(() => {
        if (timestampFilterFormField.value !== previousTimestampFilters.current) {
            setTimestampFilters(timestampFilterFormField.value || []);
            setExternalTimestampFilters(timestampFilterFormField.value || []);
        }
    }, [timestampFilterFormField.value]);

    useEffect(() => {
        if (previousTimestampFilters.current !== timestampFilters) {
            previousTimestampFilters.current = timestampFilters;
            if (!isEqual(timestampFilters, externalTimestampFilters)) {
                timestampFilterFormField.setValue(timestampFilters, undefined, true);
            }
        }
    }, [timestampFilters]);

    const resetTempFilterInput = () => {
        setTempColumn("");
        setTempFilterOperation("");
        setTempFilterValue("");
    };

    // TIMESTAMP has a richer required-value shape than the plain scalar every other type uses.
    const hasRequiredFilterValue = () => {
        if (getFilterType() !== "TIMESTAMP") {
            return tempFilterValue !== "" && tempFilterValue !== null && typeof tempFilterValue !== "undefined";
        }
        if (!tempFilterValue) {
            return false;
        }
        if (tempFilterValue.keyword) {
            return true;
        }
        return tempFilterOperation === "BETWEEN"
            ? !!tempFilterValue.start && !!tempFilterValue.end
            : !!tempFilterValue.timestamp;
    };

    // Memoized on the underlying filter arrays (not recomputed as a fresh array reference every
    // render) - Select's componentDidUpdate treats any new `options` reference as a real change
    // and re-triggers itself, so an unstable reference here caused runaway update loops.
    const combinedFilters = useMemo(() => [
        ...(numericFilters || []),
        ...(stringFilters || []),
        ...(timestampFilters || []),
        ...(timestampRangeFilters || [])
    ], [numericFilters, stringFilters, timestampFilters, timestampRangeFilters]);
    const isColumnAlreadyFiltered = (columnName) => combinedFilters.some(f => f.columnName === columnName);

    // Only timestamp filters can be DYNAMIC - everything else is implicitly static.
    const appliedFilters = useMemo(() => mode === "DYNAMIC"
        ? combinedFilters.filter(f => f.valueMode === "DYNAMIC")
        : combinedFilters.filter(f => f.valueMode !== "DYNAMIC"), [mode, combinedFilters]);

    // Only timestamp columns can be dynamic, and a column already filtered (static or dynamic)
    // can't be filtered again from either card.
    const availableColumnOptionsMemo = useMemo(() => {
        if (!options) {
            return [];
        }
        return options.filter(field =>
            field.type !== "ARRAY_NUMBER" &&
            (mode === "STATIC" || field.type === "TIMESTAMP") &&
            !isColumnAlreadyFiltered(field.name));
    }, [options, mode, combinedFilters]);
    const availableColumnOptions = () => availableColumnOptionsMemo;

    const filterType = useMemo(() => {
        if (!options) {
            return null;
        }
        return options.find(option => option.name === tempColumn)?.type;
    }, [options, tempColumn]);

    const filterColumnsOperationsMemo = useMemo(() => filterOperations[filterType] ?? [], [filterType]);
    const getFilterColumnsOperations = () => filterColumnsOperationsMemo;

    const onAdd = () => {
        if (tempColumn === "" || tempFilterOperation === "" || tempFilterOperation === null || tempFilterOperation === undefined
            || !hasRequiredFilterValue()) {
            dispatch(addErrorNotification(
                {
                    title: t(`error.validation.required.title`),
                    content: t(`error.validation.required.filter`),
                }));
            return;
        }

        if (isColumnAlreadyFiltered(tempColumn)) {
            dispatch(addErrorNotification(
                {
                    title: t(`error.validation.required.title`),
                    content: t(`error.validation.duplicate.filter`),
                }));
            return;
        }

        const type = getFilterType();
        if (type === "NUMBER" || type === "EPOCH_NUMBER") {
            setNumericFilters([...numericFilters, {
                columnName: tempColumn, operation: tempFilterOperation, number: tempFilterValue
            }]);
        }
        if (type === "ARRAY_NUMBER") {
            if (!!tempArrayOperation) {
                setNumericFilters([...numericFilters, {
                    columnName: tempColumn,
                    operation: tempFilterOperation,
                    arrayOperation: tempArrayOperation,
                    number: tempFilterValue
                }]);
            } else {
                setNumericFilters([...numericFilters, {
                    columnName: tempColumn, operation: tempFilterOperation, number: tempFilterValue
                }]);
            }
        }
        if (type === "STRING") {
            setStringFilters([...stringFilters, {
                columnName: tempColumn, operation: tempFilterOperation, value: tempFilterValue
            }]);
        }
        if (type === "TIMESTAMP" && tempFilterOperation === "BETWEEN") {
            if (tempFilterValue.keyword) {
                setTimeRangeFilters([...timestampRangeFilters, {
                    columnName: tempColumn, valueMode: mode, keyword: tempFilterValue.keyword
                }]);
            } else {
                const startDate = stringToDate(tempFilterValue.start, false, true);
                const endDate = stringToDate(tempFilterValue.end, false, true);
                if (startDate > endDate) {
                    dispatch(addErrorNotification(
                        {
                            title: t(`error.validation.required.title`),
                            content: t(`error.validation.range.order`),
                        }));
                    return;
                }
                // Already wire-ready ISO - no reformatting needed.
                setTimeRangeFilters([...timestampRangeFilters, {
                    columnName: tempColumn, valueMode: mode,
                    start: tempFilterValue.start, end: tempFilterValue.end
                }]);
            }
        }
        if (type === "TIMESTAMP" && tempFilterOperation !== "BETWEEN") {
            if (tempFilterValue.keyword) {
                setTimestampFilters([...timestampFilters, {
                    columnName: tempColumn, operation: tempFilterOperation, valueMode: mode, keyword: tempFilterValue.keyword
                }]);
            } else {
                // Already wire-ready ISO (app-configured-zone offset) - no reformatting needed.
                setTimestampFilters([...timestampFilters, {
                    columnName: tempColumn, operation: tempFilterOperation, valueMode: mode,
                    timestamp: tempFilterValue.timestamp
                }]);
            }
        }
        if (type === "OBJECT" || type === "ARRAY" || type === "BOOLEAN" || type === "TABLE") {
            console.error("Ignoring addFilter action. Unsupported filter type: " + type);
            return;
        }
        if (type === "" || type === undefined || type === null || type === "UNDEFINED") {
            console.warn("Ignoring addFilter action. Reason: Unspecified filter type: " + type + " for column: " + tempColumn);
            return;
        }
        resetTempFilterInput();
    };

    const getFormatter = (column) => {
        const columnFilterType = getFilterTypeByColumnName(column.columnName);
        switch (columnFilterType) {
            case 'NUMBER':
                return `${column.columnName}: ${column.operation} ${column.number}`;
            case 'EPOCH_NUMBER':
                // Display-only tag label - pretty, not the ISO/offset wire format.
                return `${column.columnName}: ${column.operation}(${column.number} as epoch of ${dateToString(new Date(column.number), false, true)})`;
            case 'STRING':
                return `${column.columnName}: ${column.operation}(${column.value})`;
            case 'BOOLEAN':
                return `${column.columnName}: ${column.operation}(${column.value})`;
            case 'TIMESTAMP': {
                const operationLabel = column.operation || "BETWEEN";
                if (column.keyword) {
                    return `${column.columnName}: ${operationLabel}(KEYWORD: ${column.keyword})`;
                } else if (!column.operation || column.operation === "BETWEEN" && (column.start || column.end)) {
                    return `${column.columnName}: BETWEEN(${dateToString(column.start, false, true)}) - ${dateToString(column.end, false, true)}`;
                } else if (!column.value && column.number) {
                    return `${column.columnName}: ${column.operation}(${dateToString(new Date(column.number), false, true)})`;
                } else {
                    return `${column.columnName}: ${column.operation}(${dateToString(column.timestamp, false, true)})`;
                }
            }
            default:
                return `${column.columnName}: ${column.operation}(${column.value})`;
        }
    };

    const getFilterType = () => filterType;

    const getFilterTypeByColumnName = (columnName) => {
        if (!options) {
            return "";
        }
        return options.find(option => option.name === columnName)?.type;
    };

    const handleTagRemove = (tag) => {
        const columnFilterType = getFilterTypeByColumnName(tag.columnName);
        switch (columnFilterType) {
            case 'EPOCH_NUMBER':
            case 'NUMBER':
                numericFilterFormField.setValue(numericFilters.filter(column => column !== tag));
                break;
            case 'STRING':
            case 'BOOLEAN':
                stringFilterFormField.setValue(stringFilters.filter(column => column !== tag));
                break;
            case 'TIMESTAMP':
                timestampFilterFormField.setValue(timestampFilters.filter(column => column !== tag));
                timeRangeFilterFormField.setValue(timestampRangeFilters.filter(column => column !== tag));
                break;
        }
    };

    return {
        tempColumn, setTempColumn,
        tempArrayOperation, setTempArrayOperation,
        tempFilterOperation, setTempFilterOperation,
        tempFilterValue, setTempFilterValue,
        appliedFilters,
        availableColumnOptions,
        getFilterType,
        getFilterColumnsOperations,
        getFormatter,
        handleTagRemove,
        onAdd,
    };
};

export default useFilterColumnsCard;
